"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import ThemeToggle from "./ThemeToggle";
import Logo from "./Logo";

const NAV = [
  { label: "About", href: "/about" },
  { label: "Work", href: "/work" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" }
];

const FOCUSABLE = 'a[href], button:not([disabled])';

export default function Header() {
  const [solid, setSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const hamburgerRef = useRef(null);
  const menuRef = useRef(null);
  const wasOpen = useRef(false);
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    // Freezing scroll via `position: fixed` (rather than `overflow: hidden`)
    // avoids breaking the header's `position: sticky` math — sticky's
    // containing-block calculation gets confused when body stops being a
    // normal scrolling container mid-scroll, which was sending the header
    // back to its unstuck, off-screen document position.
    const scrollY = window.scrollY;
    const openedOnPathname = pathname;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      // Only restore the old scroll position if the menu closed without
      // navigating (Escape, backdrop, re-clicking the hamburger). If the
      // user tapped a nav link, the pathname has already changed and the
      // new page should stay wherever it landed (the top), not jump back
      // to the previous page's scroll offset.
      if (pathnameRef.current === openedOnPathname) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) {
      wasOpen.current = true;
      const first = menuRef.current?.querySelector(FOCUSABLE);
      first?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      hamburgerRef.current?.focus();
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = Array.from(menuRef.current?.querySelectorAll(FOCUSABLE) || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const isActive = (href) => pathname === href || (href === "/work" && pathname.startsWith("/work/"));

  return (
    <>
      <header className={"px-header" + (solid ? " is-solid" : "") + (menuOpen ? " is-menu-open" : "")}>
        <div className={"px-header-bar" + (solid ? " is-solid" : "")}>
          <Link href="/" aria-label="Pixels and Pencils home" style={{ display: "flex", alignItems: "center" }}>
            <Logo width={89} height={24} style={{ height: 24, width: "auto", display: "block" }} aria-hidden="true" />
          </Link>
          <nav className="px-nav" style={{ display: "contents" }}>
            {NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-nav-link"
                aria-current={isActive(item.href) ? "page" : undefined}
              >
                {item.label.toUpperCase()}
              </Link>
            ))}
          </nav>
          <button
            ref={hamburgerRef}
            type="button"
            className={"px-hamburger" + (menuOpen ? " is-open" : "")}
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="px-mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      <div
        id="px-mobile-menu"
        ref={menuRef}
        className={"px-mobile-menu" + (menuOpen ? " is-open" : "")}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        <nav style={{ display: "flex", flexDirection: "column" }}>
          {NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="px-mobile-link"
              aria-current={isActive(item.href) ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="px-mobile-menu-footer">
          <a href="https://www.linkedin.com/company/pixels-and-pencils/" target="_blank" rel="noopener">LinkedIn ↗</a>
          <a href="https://www.instagram.com/pixels.and/" target="_blank" rel="noopener">Instagram ↗</a>
          <ThemeToggle />
        </div>
      </div>

      <ThemeToggle className="px-theme-toggle-fixed" />
    </>
  );
}
